/* eslint-disable react/no-array-index-key */
import { type CarouselUpdateEventDetail, PCarousel } from '@porsche-design-system/components-react';
import { borderRadiusMedium } from '@porsche-design-system/components-react/styles';
import { useRef, useState, useMemo, useCallback } from 'react';
import Lightbox, { type ImagesListType } from 'react-spring-lightbox';
import styled from 'styled-components';
import { type VehicleCarouselProps } from '../../context';
import ImageOverlay from './ImageOverlay';
import LightBoxFooter from './LightBoxFooter';
import ThumbnailCarousel from './ThumbnailCarousel';
import useCarouselStyle from './useCarouselStyle';

const Container = styled.div`
    position: relative;
    max-height: 69vh;
`;

const SingleImageContainer = styled.div`
    aspect-ratio: 16 / 9;
    flex: none;
    width: 100%;
    height: auto;
    max-height: 69vh;
`;

const MainImage = styled.img`
    aspect-ratio: 16 / 9;
    display: block;
    object-fit: cover;
    width: 100%;
    height: auto;
    border-radius: ${borderRadiusMedium};
`;

const StyledLightbox = styled(Lightbox)`
    img.lightbox-image {
        object-fit: cover;
        width: 100vw;
        height: auto;
        aspect-ratio: 16/9;
        max-height: 100dvh !important; // override lightbox max-height
        display: block;
    }
`;

const VehicleCarousel = ({ fallbackSource, sources }: VehicleCarouselProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    useCarouselStyle(containerRef, 'medium');

    const [isFullscreen, setIsFullscreen] = useState(false);
    const [imageIndex, setImageIndex] = useState(0);

    const actions = useMemo(
        () => ({
            lightbox: {
                next: () => {
                    if (imageIndex + 1 < sources.length) {
                        setImageIndex(imageIndex + 1);
                    }
                },
                previous: () => {
                    if (imageIndex > 0) {
                        setImageIndex(imageIndex - 1);
                    }
                },
                close: () => setIsFullscreen(false),
            },
        }),
        [imageIndex, setImageIndex, setIsFullscreen, sources.length]
    );

    const images: ImagesListType = useMemo(
        () =>
            sources.length
                ? sources.map<ImagesListType[number]>(source => ({
                      src: encodeURI(source),
                      alt: 'image',
                      loading: 'lazy',
                  }))
                : [{ src: encodeURI(fallbackSource), alt: 'image', loading: 'lazy' }],
        [fallbackSource, sources]
    );

    const onUpdate = useCallback(
        (e: CustomEvent<CarouselUpdateEventDetail>) => setImageIndex(e.detail.activeIndex),
        []
    );

    if (images.length === 1) {
        return (
            <>
                <Container>
                    <ImageOverlay isFullscreen={isFullscreen} setIsFullscreen={setIsFullscreen} />
                    <SingleImageContainer>
                        <MainImage alt="Vehicle" src={images[0]?.src} />
                    </SingleImageContainer>
                </Container>
                <StyledLightbox
                    currentIndex={imageIndex}
                    images={images}
                    isOpen={isFullscreen}
                    onClose={actions.lightbox.close}
                    onNext={actions.lightbox.next}
                    onPrev={actions.lightbox.previous}
                    renderImageOverlay={() => (
                        <ImageOverlay isFullscreen={isFullscreen} setIsFullscreen={setIsFullscreen} />
                    )}
                    style={{ backgroundColor: 'rgb(216 216 219 / 1)' }}
                />
            </>
        );
    }

    return (
        <>
            <Container ref={containerRef}>
                <ImageOverlay isFullscreen={isFullscreen} setIsFullscreen={setIsFullscreen} />
                <PCarousel
                    activeSlideIndex={imageIndex}
                    onUpdate={onUpdate}
                    pagination={false}
                    rewind={false}
                    slidesPerPage={1}
                    width="extended"
                >
                    {images.map(({ src }, index) => (
                        <SingleImageContainer key={index}>
                            <MainImage
                                alt="Vehicle"
                                onError={e => {
                                    e.currentTarget.src = fallbackSource;
                                }}
                                src={src}
                            />
                        </SingleImageContainer>
                    ))}
                </PCarousel>
            </Container>
            <ThumbnailCarousel activeIndex={imageIndex} images={sources} setActiveIndex={setImageIndex} />
            <StyledLightbox
                currentIndex={imageIndex}
                images={images}
                isOpen={isFullscreen}
                onClose={actions.lightbox.close}
                onNext={actions.lightbox.next}
                onPrev={actions.lightbox.previous}
                renderFooter={() => (
                    <LightBoxFooter>
                        <ThumbnailCarousel
                            activeIndex={imageIndex}
                            images={sources}
                            setActiveIndex={setImageIndex}
                            showNavButtons={false}
                        />
                    </LightBoxFooter>
                )}
                renderImageOverlay={() => (
                    <ImageOverlay isFullscreen={isFullscreen} setIsFullscreen={setIsFullscreen} />
                )}
                style={{ backgroundColor: 'rgb(216 216 219 / 1)' }}
            />
        </>
    );
};

export default VehicleCarousel;
